import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Share2, Copy, Check, Loader2, Link2, Eye } from 'lucide-react';
import { useGameStore } from '@/store/gameStore';
import { shareReplay } from '@/utils/replayService';
import { toast } from 'sonner';

const ShareDialog = ({ isOpen, onClose, gameData = null }) => {
  const { 
    position, 
    history, 
    playerColor, 
    aiDifficulty, 
    gameStatus, 
    winner,
    moveAnalysis,
    boardTheme 
  } = useGameStore();
  
  const [isSharing, setIsSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [gameName, setGameName] = useState('');

  // Use provided gameData or current game state
  const currentGame = gameData || {
    fen: position,
    history,
    playerColor,
    difficulty: aiDifficulty,
    moveAnalysis,
    result: getGameResult()
  };

  function getGameResult() {
    if (!gameStatus || gameStatus === 'playing' || gameStatus === 'idle') return null;
    if (gameStatus === 'draw' || gameStatus === 'stalemate') return 'draw';
    if (winner === playerColor) return 'win';
    return 'loss';
  }

  const handleShare = async () => {
    setIsSharing(true);
    try {
      const result = await shareReplay({
        name: gameName || `Game ${new Date().toLocaleDateString()}`,
        ...currentGame
      });
      
      setShareUrl(result.share_url);
      toast.success('Replay link created!');
    } catch (error) {
      toast.error(error.message || 'Failed to create share link');
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleClose = () => {
    setShareUrl('');
    setCopied(false);
    setGameName('');
    onClose();
  };

  const getResultText = () => {
    const result = currentGame.result;
    if (!result) return 'In Progress';
    if (result === 'win') return 'Victory';
    if (result === 'loss') return 'Defeat';
    return 'Draw';
  };

  const getResultColor = () => {
    const result = currentGame.result;
    if (!result) return 'text-cyan-400';
    if (result === 'win') return 'text-green-400';
    if (result === 'loss') return 'text-red-400';
    return 'text-gray-400';
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className={`
          max-w-md
          ${boardTheme === 'cosmic'
            ? 'bg-slate-900 border-purple-500/30 text-white'
            : 'bg-amber-50 border-amber-300 text-gray-900'
          }
        `}
        data-testid="share-dialog"
      >
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${boardTheme === 'cosmic' ? 'text-cyan-400' : 'text-amber-800'}`}>
            <Share2 className="w-5 h-5" />
            Share Replay
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Game Info */}
          <div className={`
            p-4 rounded-lg
            ${boardTheme === 'cosmic' ? 'bg-slate-800/50' : 'bg-amber-100'}
          `}>
            <div className="flex justify-between items-center mb-3">
              <span className={boardTheme === 'cosmic' ? 'text-gray-400' : 'text-gray-600'}>
                Game Result
              </span>
              <span className={`font-semibold ${getResultColor()}`}>
                {getResultText()}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className={boardTheme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}>
                Moves played
              </span>
              <span className="font-mono">{currentGame.history?.length || 0}</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className={boardTheme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}>
                Played as
              </span>
              <span className="capitalize">{currentGame.playerColor}</span>
            </div>
          </div>

          {!shareUrl ? (
            <>
              {/* Game Name Input */}
              <div>
                <label className={`text-sm mb-2 block ${boardTheme === 'cosmic' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Give this replay a name (optional)
                </label>
                <Input
                  value={gameName}
                  onChange={(e) => setGameName(e.target.value)}
                  placeholder="e.g., Epic Queen Sacrifice"
                  className={`
                    ${boardTheme === 'cosmic'
                      ? 'bg-slate-800 border-purple-500/30 text-white placeholder-gray-500'
                      : 'bg-white border-amber-300 placeholder-gray-400'
                    }
                  `}
                  data-testid="replay-name-input"
                />
              </div>

              {/* Share Button */}
              <Button
                onClick={handleShare}
                disabled={isSharing || currentGame.history?.length === 0}
                className={`
                  w-full
                  ${boardTheme === 'cosmic'
                    ? 'bg-cyan-600 hover:bg-cyan-700'
                    : 'bg-amber-600 hover:bg-amber-700'
                  }
                  text-white
                `}
                data-testid="create-share-link-btn"
              >
                {isSharing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating link...
                  </>
                ) : (
                  <>
                    <Link2 className="w-4 h-4 mr-2" />
                    Create Shareable Link
                  </>
                )}
              </Button>

              {currentGame.history?.length === 0 && (
                <p className={`text-xs text-center ${boardTheme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}`}>
                  Play some moves first to share the game
                </p>
              )}
            </>
          ) : (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {/* Success Message */}
                <div className={`
                  p-3 rounded-lg text-center
                  ${boardTheme === 'cosmic' ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'}
                `}>
                  <Check className="w-6 h-6 mx-auto mb-2" />
                  Replay link created!
                </div>

                {/* Share URL */}
                <div className="space-y-2">
                  <label className={`text-sm ${boardTheme === 'cosmic' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Share this link with friends
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={shareUrl}
                      readOnly
                      className={`
                        flex-1 font-mono text-sm
                        ${boardTheme === 'cosmic'
                          ? 'bg-slate-800 border-purple-500/30 text-cyan-400'
                          : 'bg-white border-amber-300 text-blue-600'
                        }
                      `}
                      data-testid="share-url-input"
                    />
                    <Button
                      onClick={handleCopy}
                      variant="outline"
                      className={`
                        px-4
                        ${boardTheme === 'cosmic'
                          ? 'border-cyan-500/50 text-cyan-400 hover:bg-cyan-900/30'
                          : 'border-amber-300 text-amber-700 hover:bg-amber-100'
                        }
                      `}
                      data-testid="copy-link-btn"
                    >
                      {copied ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Info */}
                <div className={`
                  flex items-center gap-2 text-xs p-3 rounded-lg
                  ${boardTheme === 'cosmic' ? 'bg-slate-800/50 text-gray-400' : 'bg-amber-50 text-gray-500'}
                `}>
                  <Eye className="w-4 h-4" />
                  <span>Anyone with this link can view and analyze the replay</span>
                </div>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
